// -----------------------------------------------------------
// FloraIQ - Vocabulario dos modulos Ambiental e Carbono
// -----------------------------------------------------------
// Apenas classificacoes de dominio (tipos, categorias, grupos). Nao contem
// registros nem numeros: as telas ainda nao tem fonte real de dado.

/** Tipos de area verde urbana adotados na classificacao. */
export const TIPOS_AREA_VERDE = [
  "Praça",
  "Parque urbano",
  "Canteiro central",
  "Rotatória",
  "Bosque",
  "Jardim público",
  "Área institucional arborizada",
  "Corredor verde",
  "Área de lazer",
  "Área de compensação",
  "Área de replantio",
  "Área de recuperação",
] as const;

/** Grupos de Unidade de Conservação (SNUC) e enquadramentos municipais. */
export const GRUPOS_UC = [
  "Proteção Integral",
  "Uso Sustentável",
  "Área verde municipal não enquadrada",
  "Área de interesse ambiental municipal",
] as const;

/** Categorias de Unidade de Conservação. */
export const CATEGORIAS_UC = [
  "Parque Natural Municipal",
  "APA",
  "ARIE",
  "RPPN",
  "Monumento Natural",
  "Refúgio de Vida Silvestre",
  "Bosque Municipal",
  "Corredor Ecológico",
] as const;

/** Tipos de projeto de carbono / ativo ambiental. */
export const TIPOS_PROJETO_CARBONO = [
  "Carbono Urbano",
  "Arborização Urbana",
  "Compensação Ambiental",
  "Replantio",
  "Recuperação de Área Degradada",
  "UC / Área Protegida",
  "Corredor Verde",
  "ESG Corporativo",
] as const;

/**
 * Metodologias de estimativa de carbono descritas como referencia.
 * Nenhuma esta implementada no calculo do FloraIQ ainda; por isso nao ha
 * status "ativa" nem faixa de precisao.
 */
export interface MetodologiaCarbonoReferencia {
  id: string;
  name: string;
  type: string;
  description: string;
  requiredData: string[];
  limitations: string;
}

export const METODOLOGIAS_CARBONO_REFERENCIA: MetodologiaCarbonoReferencia[] = [
  {
    id: "dap",
    name: "Modelo simplificado por DAP",
    type: "Estimativa individual",
    description:
      "Estimativa de biomassa arbórea a partir do DAP (diâmetro à altura do peito) e de fatores médios para as espécies cadastradas.",
    requiredData: ["DAP", "Espécie cadastrada"],
    limitations: "Não considera altura real, condição fitossanitária, idade nem fatores locais.",
  },
  {
    id: "dap-altura",
    name: "Modelo por DAP + altura",
    type: "Estimativa individual",
    description:
      "Incorpora a altura total do exemplar arbóreo junto ao DAP para estimar volume e biomassa.",
    requiredData: ["DAP", "Altura total", "Espécie cadastrada"],
    limitations: "Requer medição de altura confiável. Pode distorcer exemplares com copa atípica.",
  },
  {
    id: "alometrica",
    name: "Modelo por espécie / grupo funcional",
    type: "Estimativa individual",
    description:
      "Equações alométricas específicas por espécie ou grupo funcional (pioneira, secundária inicial, secundária tardia, climácica).",
    requiredData: ["DAP", "Altura", "Espécie identificada", "Grupo funcional"],
    limitations: "Exige identificação botânica segura. Nem toda espécie tem equação alométrica publicada.",
  },
  {
    id: "fator-urbano",
    name: "Fator médio urbano",
    type: "Estimativa por área",
    description:
      "Fator médio de biomassa por hectare para arborização urbana, a partir de inventários regionais e literatura.",
    requiredData: ["Área (ha)", "Densidade de exemplares", "Tipo de vegetação"],
    limitations: "Não considera variação individual. Indicado para corredores e áreas extensas.",
  },
  {
    id: "area-verde",
    name: "Estimativa por área verde",
    type: "Estimativa por área",
    description:
      "Fatores médios de biomassa por hectare conforme o tipo de área (parque, praça, APP, UC).",
    requiredData: ["Área (ha)", "Tipo de área verde", "Número de exemplares"],
    limitations: "Varia conforme o manejo da área. Requer ajuste por bioma e região.",
  },
  {
    id: "compensacao",
    name: "Estimativa preliminar para compensação",
    type: "Compensação",
    description:
      "Considera mudas plantadas, taxa de sobrevivência e crescimento esperado para compensação ambiental.",
    requiredData: ["Quantidade de mudas", "Taxa de sobrevivência", "Idade das mudas", "Espécies"],
    limitations: "Alta incerteza pela variação de sobrevivência e crescimento. Revisar periodicamente.",
  },
];
